import { useEffect, useMemo, useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatearRut, validarRutChileno } from '../../lib/rut';
import { SearchableCombobox } from '../ui/searchable-combobox';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

// Iter 390 — Proveedor (Alegra) buscable por RUT o nombre en la misma celda. Reutiliza lib/rut.js (módulo 11 + 00000000-A).
let cacheProveedores = null;

export async function cargarProveedores(force = false) {
  if (cacheProveedores && !force) return cacheProveedores;
  const r = await api.get('/contabilidad/egresos/proveedores-combo');
  cacheProveedores = r.data.proveedores || [];
  return cacheProveedores;
}

const rutNorm = (r) => String(r || '').replace(/[^0-9kK]/g, '').toUpperCase();

export function ProveedorCombobox({ value, onChange, disabled = false, testid = 'proveedor-combobox' }) {
  const [lista, setLista] = useState(cacheProveedores || []);
  const [crear, setCrear] = useState(false);

  useEffect(() => { cargarProveedores().then(setLista).catch(() => toast.error('No se pudo cargar la lista de proveedores')); }, []);

  const items = useMemo(() => lista.map((p) => ({ value: rutNorm(p.rut), label: p.nombre, subLabel: p.rut, rut: p.rut, alegra_id: p.alegra_id })), [lista]);

  const onCreado = (nuevo) => {
    cacheProveedores = [...(cacheProveedores || []), nuevo];
    setLista(cacheProveedores);
    setCrear(false);
    onChange({ rut: nuevo.rut, nombre: nuevo.nombre, alegra_id: nuevo.alegra_id });
  };

  return (
    <div className="space-y-1">
      <SearchableCombobox items={items} value={value ? rutNorm(value) : ''} disabled={disabled} testid={testid}
        placeholder="Buscar por RUT o nombre…" emptyMessage="Sin coincidencias"
        onChange={(item) => onChange(item ? { rut: item.rut, nombre: item.label, alegra_id: item.alegra_id } : null)} />
      {!disabled && !crear && (
        <button type="button" className="text-[11px] text-indigo-700 hover:underline inline-flex items-center gap-1" onClick={() => setCrear(true)} data-testid={`${testid}-crear-link`}>
          <Plus className="w-3 h-3" /> ¿No está? Crear proveedor en el maestro (y en Alegra)
        </button>
      )}
      {crear && <ProveedorNuevoInline onCreado={onCreado} onCancel={() => setCrear(false)} testid={`${testid}-nuevo`} />}
    </div>
  );
}

function ProveedorNuevoInline({ onCreado, onCancel, testid }) {
  const [f, setF] = useState({ rut: '', nombre: '', email: '' });
  const [saving, setSaving] = useState(false);
  const rutOk = validarRutChileno(f.rut);
  const rutFmt = formatearRut(f.rut);

  const guardar = async () => {
    if (!rutOk || !rutFmt) { toast.error('RUT inválido: debe cumplir módulo 11 (formato 00000000-A)'); return; }
    if (!f.nombre.trim()) { toast.error('Indica la razón social'); return; }
    setSaving(true);
    try {
      const r = await api.post('/proveedores/alta-rapida', { rut: rutFmt, nombre: f.nombre.trim(), email: f.email.trim() });
      toast.success(`Proveedor ${r.data.nombre} (${r.data.rut}) creado en Alegra y en el maestro`);
      onCreado(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear el proveedor');
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded border border-indigo-200 bg-indigo-50/40 p-2 grid grid-cols-2 gap-2" data-testid={testid}>
      <div>
        <Input placeholder="RUT (00000000-A)" value={f.rut} onChange={(e) => setF({ ...f, rut: e.target.value })}
          className={`text-sm font-mono ${f.rut && !rutOk ? 'border-red-400 bg-red-50/40' : ''}`} data-testid={`${testid}-rut`} />
        {f.rut && !rutOk && <p className="text-[10px] text-red-600 mt-0.5" data-testid={`${testid}-rut-error`}>RUT inválido (módulo 11)</p>}
        {rutOk && rutFmt && <p className="text-[10px] text-emerald-700 mt-0.5">Se guardará como {rutFmt}</p>}
      </div>
      <Input placeholder="Razón social" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} className="text-sm" data-testid={`${testid}-nombre`} />
      <Input placeholder="Email (opcional)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="text-sm" data-testid={`${testid}-email`} />
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button type="button" size="sm" onClick={guardar} disabled={saving || !rutOk || !f.nombre.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`${testid}-guardar`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Crear proveedor'}
        </Button>
      </div>
    </div>
  );
}
